import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { readConsent, writeConsent } from "@/lib/consent-mode";

export function CookieBanner() {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    const existing = readConsent();
    if (existing) return;
    // Pokaż banner dopiero po pierwszej interakcji lub po 4s — nie blokuje LCP/FCP.
    let shown = false;
    const reveal = () => {
      if (shown) return;
      shown = true;
      setShow(true);
    };
    const t = setTimeout(reveal, 4000);
    const events: Array<keyof WindowEventMap> = ["scroll", "pointerdown", "keydown", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, reveal, { once: true, passive: true }));
    return () => {
      clearTimeout(t);
      events.forEach((ev) => window.removeEventListener(ev, reveal));
    };
  }, []);

  const accept = (value: { analytics: boolean; marketing: boolean }) => {
    writeConsent(value);
    setShow(false);
  };

  const openSettings = () => {
    const existing = readConsent();
    if (existing) {
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
    }
    setShowDetails(true);
    setShow(true);
  };

  // Eksponujemy globalny handler — przycisk w stopce może otworzyć ustawienia.
  useEffect(() => {
    (window as unknown as { synctechOpenCookieSettings?: () => void }).synctechOpenCookieSettings = openSettings;
    return () => {
      delete (window as unknown as { synctechOpenCookieSettings?: () => void }).synctechOpenCookieSettings;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 24, stiffness: 220 }}
          className="fixed bottom-4 left-4 right-4 z-[100] mx-auto max-w-3xl"
          role="dialog"
          aria-modal="true"
          aria-label="Zgoda na pliki cookies"
        >
          <div className="glass relative flex flex-col gap-4 rounded-2xl border border-border p-5 shadow-elegant">
            <div className="flex items-start gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                <Cookie className="h-5 w-5" />
              </span>
              <div className="flex-1 text-sm">
                <p className="font-semibold">Ustawienia plików cookies</p>
                <p className="mt-1 text-muted-foreground">
                  Używamy cookies, aby strona działała poprawnie, mierzyć ruch (Google Analytics) i prowadzić kampanie reklamowe (Google Ads).
                  Możesz zaakceptować wszystkie, tylko niezbędne lub dostosować wybór.{" "}
                  <Link to="/polityka-prywatnosci" className="font-medium text-primary underline">
                    Polityka prywatności
                  </Link>
                  .
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="inline-flex w-fit items-center gap-1 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              aria-expanded={showDetails}
            >
              {showDetails ? (
                <>
                  Ukryj szczegóły <ChevronUp className="h-3.5 w-3.5" />
                </>
              ) : (
                <>
                  Dostosuj wybór <ChevronDown className="h-3.5 w-3.5" />
                </>
              )}
            </button>

            {showDetails && (
              <div className="grid gap-2 rounded-xl border border-border/60 bg-background/40 p-3 text-xs">
                <label className="flex cursor-not-allowed items-start gap-3 opacity-70">
                  <input type="checkbox" checked disabled className="mt-0.5 h-4 w-4 accent-primary" />
                  <span>
                    <span className="font-semibold">Niezbędne (zawsze aktywne)</span>
                    <span className="block text-muted-foreground">Wymagane do działania serwisu (sesja, zabezpieczenia, preferencje cookies).</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="mt-0.5 h-4 w-4 cursor-pointer accent-primary"
                  />
                  <span>
                    <span className="font-semibold">Analityczne</span>
                    <span className="block text-muted-foreground">Google Analytics 4, Microsoft Clarity — anonimowe statystyki ruchu i heatmapy.</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    className="mt-0.5 h-4 w-4 cursor-pointer accent-primary"
                  />
                  <span>
                    <span className="font-semibold">Marketingowe</span>
                    <span className="block text-muted-foreground">Google Ads, Facebook Pixel — pomiar konwersji i remarketing.</span>
                  </span>
                </label>
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={() => accept({ analytics: false, marketing: false })}
                className="rounded-full border border-border bg-background/60 px-4 py-2 text-xs font-semibold transition hover:bg-accent"
              >
                Tylko niezbędne
              </button>
              {showDetails && (
                <button
                  onClick={() => accept({ analytics, marketing })}
                  className="rounded-full border border-primary/40 bg-primary/10 px-4 py-2 text-xs font-semibold text-primary transition hover:bg-primary/20"
                >
                  Zapisz wybór
                </button>
              )}
              <button
                onClick={() => accept({ analytics: true, marketing: true })}
                className="rounded-full bg-gradient-to-r from-primary to-primary-glow px-4 py-2 text-xs font-semibold text-primary-foreground shadow-glow transition hover:scale-[1.03]"
              >
                Akceptuję wszystkie
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
